// src/components/communications/SMSPage.js
import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Select, MenuItem,
  FormControl, InputLabel, Alert, Autocomplete, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, 
  Paper, Checkbox, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Tooltip, CircularProgress, Snackbar, TablePagination, Grid
} from '@mui/material';
import { 
  Send as SendIcon, 
  Add as AddIcon,
  Sms as SmsIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  BarChart as BarChartIcon
} from '@mui/icons-material';
import { communicationAPI, academicAPI, studentAPI, schoolAPI } from '../../services/api';

const SMSPage = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Individual SMS states
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentOptions, setStudentOptions] = useState([]);
  const [individualMessage, setIndividualMessage] = useState('');
  const [showIndividualDialog, setShowIndividualDialog] = useState(false);
  
  // Bulk SMS states
  const [recipients, setRecipients] = useState('all');
  const [classStreams, setClassStreams] = useState([]);
  const [selectedClassStreams, setSelectedClassStreams] = useState([]);
  const [bulkMessage, setBulkMessage] = useState('');
  const [includeBalance, setIncludeBalance] = useState(false);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [schoolName, setSchoolName] = useState('');
  const [showStudentSelector, setShowStudentSelector] = useState(false);
  const [allStudents, setAllStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [sentSMS, setSentSMS] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalSMS, setTotalSMS] = useState(0);
  const [selectedSms, setSelectedSms] = useState(null);
  const [showSmsDialog, setShowSmsDialog] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [smsStatistics, setSmsStatistics] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  useEffect(() => {
    fetchClassStreams();
    fetchStudents();
    fetchSmsStatistics();
    schoolAPI.get().then(r => setSchoolName(r.data?.name || '')).catch(() => {});
  }, []);

  useEffect(() => {
    fetchSmsLogs();
  }, [page, rowsPerPage, dateFrom, dateTo]);

  useEffect(() => {
    fetchSmsStatistics();
  }, [selectedYear]);

  useEffect(() => {
    setSelectedStudents([]);
    setAllStudents([]);
  }, [recipients, selectedClassStreams]);

  const fetchClassStreams = async () => {
    try {
      const response = await academicAPI.getClassStreams();
      setClassStreams(response.data);
    } catch (error) {
      console.error('Error fetching class streams:', error);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await studentAPI.getActive();
      const students = response.data.data || response.data;
      setStudentOptions(students.map(s => ({
        id: s.id,
        label: `${s.full_name} - ${s.class_stream_name}`,
        ...s
      })));
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  const fetchSmsStatistics = async () => {
    try {
      const response = await communicationAPI.getSmsStatistics({ year: selectedYear });
      setSmsStatistics(response.data.data || []);
    } catch (error) {
      console.error('Error fetching SMS statistics:', error);
    }
  };

  const fetchSmsLogs = async () => {
    try {
      const response = await communicationAPI.getSmsLogs({
        page: page + 1,
        per_page: rowsPerPage,
        date_from: dateFrom,
        date_to: dateTo
      });
      setSentSMS(response.data.data || []);
      setTotalSMS(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching SMS logs:', error);
    }
  };

  const fetchStudentsForSelection = async () => {
    setLoading(true);
    try {
      let students = [];
      
      if (recipients === 'all') {
        // For 'all', get all active students, optionally filtered by selected classes
        const response = await communicationAPI.getFilteredStudents({
          filter_type: 'class_stream',
          class_stream_ids: selectedClassStreams.length > 0 ? selectedClassStreams : classStreams.map(cs => cs.id)
        });
        students = response.data.students;
      } else if (recipients === 'class_stream') {
        if (selectedClassStreams.length === 0) {
          setSnackbar({ open: true, message: 'Please select at least one class', severity: 'warning' });
          setLoading(false);
          return;
        }
        const response = await communicationAPI.getFilteredStudents({
          filter_type: 'class_stream',
          class_stream_ids: selectedClassStreams
        });
        students = response.data.students;
      } else if (recipients === 'defaulters') {
        const response = await communicationAPI.getFilteredStudents({
          filter_type: 'defaulters',
          class_stream_ids: selectedClassStreams.length > 0 ? selectedClassStreams : undefined
        });
        students = response.data.students;
      } else if (recipients === 'overdue') {
        const response = await communicationAPI.getFilteredStudents({
          filter_type: 'overdue',
          class_stream_ids: selectedClassStreams.length > 0 ? selectedClassStreams : undefined
        });
        students = response.data.students;
      }
      
      setAllStudents(students);
      setSelectedStudents(students.map(s => s.id));
      setShowStudentSelector(true);
    } catch (error) {
      setSnackbar({ 
        open: true, 
        message: 'Error fetching students: ' + (error.response?.data?.message || 'Please try again'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleIndividualSMS = async () => {
    if (!selectedStudent || !individualMessage) {
      setSnackbar({ open: true, message: 'Please select a student and enter a message', severity: 'warning' });
      return;
    }

    setLoading(true);
    try {
      const response = await communicationAPI.sendSmsToParent({
        student_id: selectedStudent.id,
        message: individualMessage
      });
      setSnackbar({ open: true, message: response.data.message || 'SMS sent successfully!', severity: 'success' });
      
      fetchSmsLogs();

      setShowIndividualDialog(false);
      setSelectedStudent(null);
      setIndividualMessage('');
    } catch (error) {
      setSnackbar({ 
        open: true, 
        message: 'Error sending SMS: ' + (error.response?.data?.error || 'Please try again'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBulkSMS = async () => {
    if (!bulkMessage) {
      setSnackbar({ open: true, message: 'Please enter a message', severity: 'warning' });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        recipients: recipients === 'all' ? 'class_stream' : recipients, // Convert 'all' to 'class_stream' for backend
        custom_message: bulkMessage,
        include_balance: includeBalance
      };

      // If specific students are selected from the student selector
      if (selectedStudents.length > 0 && allStudents.length > 0 && selectedStudents.length < allStudents.length) {
        payload.student_ids = selectedStudents;
      } 
      // If class streams are selected for filtering
      else if (selectedClassStreams.length > 0) {
        payload.class_stream_ids = selectedClassStreams;
      }
      // For class_stream recipient type, class selection is required
      else if (recipients === 'class_stream') {
        setSnackbar({ open: true, message: 'Please select at least one class for class stream recipients', severity: 'warning' });
        setLoading(false);
        return;
      }
      // For 'all' recipients, if no classes selected, send to all classes
      else if (recipients === 'all') {
        payload.class_stream_ids = classStreams.map(cs => cs.id);
      }

      const response = await communicationAPI.sendSms(payload);
      setSnackbar({ open: true, message: response.data.message || 'Bulk SMS sent successfully!', severity: 'success' });
      
      fetchSmsLogs();

      setShowBulkDialog(false);
      setBulkMessage('');
      setSelectedClassStreams([]);
      setSelectedStudents([]);
    } catch (error) {
      setSnackbar({ 
        open: true, 
        message: 'Error sending bulk SMS: ' + (error.response?.data?.error || 'Please try again'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStudentToggle = (studentId) => {
    setSelectedStudents(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleSelectAll = () => {
    setSelectedStudents(allStudents.map(s => s.id));
  };

  const handleDeselectAll = () => {
    setSelectedStudents([]);
  };

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  const handleClearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewSms = (sms) => {
    setSelectedSms(sms);
    setShowSmsDialog(true);
  };

  return (
    <Box>
      <Box mb={3} display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center" gap={2}>
          <SmsIcon color="primary" sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h4" fontWeight={700}>
              SMS Management
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Send individual and bulk SMS messages to guardians
            </Typography>
          </Box>
        </Box>
      </Box>

      <Card elevation={3}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ flexGrow: 1 }}>
              <Tab 
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <span>SMS Logs</span>
                    <Chip label={totalSMS} size="small" color="primary" />
                  </Box>
                } 
              />
              <Tab 
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <BarChartIcon fontSize="small" />
                    <span>Statistics</span>
                  </Box>
                } 
              />
            </Tabs>
            <Box display="flex" gap={1}>
              <Tooltip title="Send Individual SMS">
                <IconButton 
                  color="secondary"
                  onClick={() => setShowIndividualDialog(true)}
                  sx={{ 
                    bgcolor: 'secondary.main', 
                    color: 'white',
                    '&:hover': { bgcolor: 'secondary.dark' }
                  }}
                >
                  <SendIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Send Bulk SMS">
                <IconButton 
                  color="primary" 
                  onClick={() => { 
                    setBulkMessage(schoolName ? `${schoolName}: ` : ''); 
                    setIncludeBalance(false); 
                    setRecipients('all');
                    setSelectedClassStreams(classStreams.map(cs => cs.id)); // Auto-select all classes for 'all' option
                    setSelectedStudents([]); 
                    setAllStudents([]); 
                    setShowBulkDialog(true); 
                  }}
                  sx={{ 
                    bgcolor: 'primary.main', 
                    color: 'white',
                    '&:hover': { bgcolor: 'primary.dark' }
                  }}
                >
                  <AddIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Date Filters */}
          {tabValue === 0 && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Date From"
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Date To"
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    variant="outlined"
                    onClick={handleClearFilters}
                    disabled={!dateFrom && !dateTo}
                    fullWidth
                  >
                    Clear Filters
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Year Filter for Statistics */}
          {tabValue === 1 && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Year</InputLabel>
                    <Select
                      value={selectedYear}
                      label="Year"
                      onChange={(e) => setSelectedYear(e.target.value)}
                    >
                      {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                        <MenuItem key={year} value={year}>{year}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          )}

          {tabValue === 0 && (
            <>
              <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Date</TableCell>
                      <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Recipients</TableCell>
                      <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Message Preview</TableCell>
                      <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Delivered</TableCell>
                      <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sentSMS.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 8, color: 'text.secondary' }}>
                          No SMS logs found. Use the icons above to send SMS.
                        </TableCell>
                      </TableRow>
                    ) : (
                      sentSMS.map((sms) => (
                        <TableRow key={sms.id} hover>
                          <TableCell>{new Date(sms.created_at).toLocaleDateString()}</TableCell>
                          <TableCell>
                            <Chip label={`${sms.recipient_count} recipients`} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                              {sms.message?.substring(0, 50)}...
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={sms.status?.toUpperCase()}
                              color={sms.status === 'sent' ? 'success' : sms.status === 'scheduled' ? 'warning' : 'error'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{sms.delivered_count}/{sms.recipient_count}</TableCell>
                          <TableCell>
                            <Tooltip title="View SMS Details">
                              <IconButton size="small" onClick={() => handleViewSms(sms)}>
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={totalSMS}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50]}
              />
            </>
          )}

          {tabValue === 1 && (
            <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Month</TableCell>
                    <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>SMS Sent</TableCell>
                    <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Batches</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {smsStatistics.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ py: 8, color: 'text.secondary' }}>
                        No SMS statistics found for {selectedYear}.
                      </TableCell>
                    </TableRow>
                  ) : (
                    smsStatistics.map((stat, index) => {
                      const [year, month] = stat.month_year.split('-');
                      const monthName = new Date(year, month - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                      return (
                        <TableRow key={index} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {monthName}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={stat.total_sent.toLocaleString()} 
                              color="primary" 
                              variant="outlined" 
                              size="small" 
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {stat.total_batches} batches
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Individual SMS Dialog */}
      <Dialog open={showIndividualDialog} onClose={() => setShowIndividualDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>Send Individual SMS</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Autocomplete
              options={studentOptions}
              value={selectedStudent}
              onChange={(e, newValue) => setSelectedStudent(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Student"
                  placeholder="Type student name..."
                  margin="normal"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {option.full_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.registration_no} - {option.class_stream_name}
                    </Typography>
                  </Box>
                </Box>
              )}
            />
            
            <TextField
              fullWidth
              label="Message"
              multiline
              rows={4}
              value={individualMessage}
              onChange={(e) => setIndividualMessage(e.target.value)}
              inputProps={{ maxLength: 160 }}
              helperText={`${individualMessage.length}/160 characters`}
              margin="normal"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setShowIndividualDialog(false)} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleIndividualSMS}
            variant="contained" 
            disabled={loading || !selectedStudent || !individualMessage}
            startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
          >
            {loading ? 'Sending...' : 'Send SMS'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk SMS Dialog */}
      <Dialog open={showBulkDialog} onClose={() => { setShowBulkDialog(false); setBulkMessage(''); setIncludeBalance(false); setSelectedClassStreams([]); setSelectedStudents([]); setAllStudents([]); }} fullWidth maxWidth="sm">
        <DialogTitle>Send Bulk SMS</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Recipients</InputLabel>
              <Select
                value={recipients}
                label="Recipients"
                onChange={(e) => {
                  const newRecipient = e.target.value;
                  setRecipients(newRecipient);
                  
                  // Auto-select all classes for 'all' option
                  if (newRecipient === 'all') {
                    setSelectedClassStreams(classStreams.map(cs => cs.id));
                  } else {
                    setSelectedClassStreams([]);
                  }
                  
                  setSelectedStudents([]);
                  setAllStudents([]);
                }}
              >
                <MenuItem value="all">All Students</MenuItem>
                <MenuItem value="class_stream">Specific Classes</MenuItem>
                <MenuItem value="defaulters">Fee Defaulters (Balance Due)</MenuItem>
                <MenuItem value="overdue">Overdue Students (Past Due Date)</MenuItem>
              </Select>
            </FormControl>

            {recipients === 'all' && (
              <Alert severity="success" sx={{ mt: 2 }}>
                This will send SMS to all active students. All classes are selected by default, but you can deselect specific classes or students below.
              </Alert>
            )}
            
            {recipients === 'defaulters' && (
              <Alert severity="info" sx={{ mt: 2 }}>
                This will send SMS to all students with outstanding fee balances. You can optionally filter by specific classes below.
              </Alert>
            )}
            
            {recipients === 'overdue' && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                This will send SMS to students with fees past their due date. You can optionally filter by specific classes below.
              </Alert>
            )}
            
            {recipients === 'class_stream' && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Select specific classes to send SMS to all students in those classes.
              </Alert>
            )}

            {(recipients === 'all' || recipients === 'class_stream' || recipients === 'defaulters' || recipients === 'overdue') && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {recipients === 'class_stream' ? 'Select Classes:' : 
                   recipients === 'all' ? 'Classes (all selected by default):' :
                   'Filter by Classes (optional):'}
                </Typography>
                <Box sx={{ maxHeight: 200, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
                  {recipients === 'all' && (
                    <Box sx={{ mb: 1, display: 'flex', gap: 1 }}>
                      <Button 
                        size="small" 
                        onClick={() => setSelectedClassStreams(classStreams.map(cs => cs.id))}
                        disabled={selectedClassStreams.length === classStreams.length}
                      >
                        Select All
                      </Button>
                      <Button 
                        size="small" 
                        onClick={() => setSelectedClassStreams([])}
                        disabled={selectedClassStreams.length === 0}
                      >
                        Deselect All
                      </Button>
                    </Box>
                  )}
                  {classStreams.map(cs => (
                    <Box key={cs.id} sx={{ display: 'flex', alignItems: 'center' }}>
                      <Checkbox
                        checked={selectedClassStreams.includes(cs.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedClassStreams(prev => [...prev, cs.id]);
                          } else {
                            setSelectedClassStreams(prev => prev.filter(id => id !== cs.id));
                          }
                        }}
                      />
                      <Typography variant="body2">{cs.display_name}</Typography>
                    </Box>
                  ))}
                </Box>
                {recipients === 'all' && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Deselect classes to exclude them from the SMS broadcast
                  </Typography>
                )}
                {recipients !== 'class_stream' && recipients !== 'all' && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Leave empty to include all {recipients === 'defaulters' ? 'fee defaulters' : 'overdue students'} from all classes
                  </Typography>
                )}
              </Box>
            )}

            <Box mt={2}>
              <Button
                variant="outlined"
                onClick={fetchStudentsForSelection}
                disabled={loading || (recipients === 'class_stream' && selectedClassStreams.length === 0)}
                fullWidth
              >
                {loading ? 'Loading...' : 
                 recipients === 'all' ? 'Select Students (All Selected by Default)' :
                 recipients === 'class_stream' ? 'Select Students from Classes' :
                 recipients === 'defaulters' ? 'Select Fee Defaulters' :
                 'Select Overdue Students'
                }
              </Button>
              {recipients === 'class_stream' && selectedClassStreams.length === 0 && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                  Please select at least one class first
                </Typography>
              )}
              {selectedStudents.length > 0 && allStudents.length > 0 && (
                <Box mt={1} display="flex" justifyContent="center">
                  <Chip 
                    label={`${selectedStudents.length} of ${allStudents.length} students selected`} 
                    color="primary" 
                  />
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
              <Checkbox
                checked={includeBalance}
                onChange={(e) => setIncludeBalance(e.target.checked)}
              />
              <Typography variant="body2">Include balance information</Typography>
            </Box>

            <TextField
              fullWidth
              label="Message"
              multiline
              rows={4}
              value={bulkMessage}
              onChange={(e) => setBulkMessage(e.target.value)}
              inputProps={{ maxLength: 160 }}
              helperText={`${bulkMessage.length}/160 characters`}
              margin="normal"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setShowBulkDialog(false); setBulkMessage(''); setIncludeBalance(false); setSelectedClassStreams([]); setSelectedStudents([]); setAllStudents([]); }} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleBulkSMS}
            variant="contained"
            disabled={loading || !bulkMessage || (recipients === 'class_stream' && selectedClassStreams.length === 0) || (recipients === 'all' && selectedClassStreams.length === 0)}
            startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
          >
            {loading ? 'Sending...' : 'Send Bulk SMS'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Student Selection Dialog */}
      <Dialog open={showStudentSelector} onClose={() => setShowStudentSelector(false)} maxWidth="md" fullWidth>
        <DialogTitle>Select Students</DialogTitle>
        <DialogContent>
          <Box mb={2}>
            <Button onClick={handleSelectAll} sx={{ mr: 1 }}>Select All</Button>
            <Button onClick={handleDeselectAll}>Deselect All</Button>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {selectedStudents.length} of {allStudents.length} students selected
            </Typography>
          </Box>
          
          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">Select</TableCell>
                  <TableCell>Student</TableCell>
                  <TableCell>Class</TableCell>
                  <TableCell>Guardian</TableCell>
                  <TableCell>Phone</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allStudents.map((student) => (
                  <TableRow key={student.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedStudents.includes(student.id)}
                        onChange={() => handleStudentToggle(student.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {student.full_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {student.registration_no}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{student.class_stream_name}</TableCell>
                    <TableCell>{student.guardian_name}</TableCell>
                    <TableCell>
                      <Chip 
                        label={student.has_phone ? "Yes" : "No"} 
                        color={student.has_phone ? "success" : "error"} 
                        size="small" 
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowStudentSelector(false)}>Cancel</Button>
          <Button onClick={() => setShowStudentSelector(false)} variant="contained">
            Confirm ({selectedStudents.length})
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* SMS Details Dialog */}
      <Dialog open={showSmsDialog} onClose={() => setShowSmsDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>SMS Details</DialogTitle>
        <DialogContent>
          {selectedSms && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Recipients: {selectedSms.recipient_count}
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                Delivered: {selectedSms.delivered_count}/{selectedSms.recipient_count}
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                Status: {selectedSms.status?.toUpperCase()}
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                Sent: {new Date(selectedSms.created_at).toLocaleString()}
              </Typography>
              {selectedSms.scheduled_at && (
                <Typography variant="subtitle2" gutterBottom>
                  Scheduled: {new Date(selectedSms.scheduled_at).toLocaleString()}
                </Typography>
              )}
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>Message:</Typography>
                <Typography variant="body2">{selectedSms.message}</Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default SMSPage;